<template>
    <CCard>
        <CCardHeader>
            <strong>{{ $t("output_performance") }}</strong>
        </CCardHeader>
        <CCardBody>
            <p v-if="error" class="output-performance__empty mb-0">
                {{ $t("could_not_load_output_performance") }}
                <span v-if="error !== true">({{ error }})</span>
            </p>
            <p
                v-else-if="outputs.length === 0 && !loading"
                class="output-performance__empty mb-0"
            >
                {{ $t("no_output_performance_in_this_period") }}
            </p>
            <CDataTable
                v-else
                :addTableClasses="['table--sm']"
                :items="rows"
                :fields="
                    traslateArrayObjects({
                        array: fields,
                        fields: ['label'],
                    })
                "
                :hover="true"
                :responsive="true"
                :clickableRows="true"
                :sorter="{ external: false, resetable: true }"
                @row-clicked="toggleReasons"
                size="sm"
            >
                <template #capped-header>
                    {{ $t("capped") }}
                    <i
                        v-c-tooltip="$t('capped_hint')"
                        class="fas fa-question-circle output-performance__hint"
                    ></i>
                </template>
                <template #pending-header>
                    {{ $t("pending") }}
                    <i
                        v-c-tooltip="$t('pending_hint')"
                        class="fas fa-question-circle output-performance__hint"
                    ></i>
                </template>
                <template #duplicate-header>
                    {{ $t("duplicate") }}
                    <i
                        v-c-tooltip="$t('duplicate_hint')"
                        class="fas fa-question-circle output-performance__hint"
                    ></i>
                </template>
                <template #client_id="{ item }">
                    <td>
                        <!--
                            `@click.stop`: la fila entera despliega los motivos
                            al clicarla, y sin esto abrir la ficha desplegaria
                            la fila al mismo tiempo.
                        -->
                        <RouterLink
                            v-if="item.client_uuid"
                            :to="outputRoute(item)"
                            @click.native.stop
                            >{{ item.client_id }}</RouterLink
                        >
                        <span v-else>{{ item.client_id }}</span>
                    </td>
                </template>
                <template #client_name="{ item }">
                    <td>
                        <span
                            class="output-performance__caret"
                            :class="
                                hasReasons(item)
                                    ? ''
                                    : 'output-performance__caret--empty'
                            "
                        >
                            {{ isExpanded(item) ? "▾" : "▸" }}
                        </span>
                        {{ item.client_name }}
                    </td>
                </template>
                <template #ok_rate="{ item }">
                    <td>
                        <div class="d-flex align-items-center">
                            <ChartGauge :value="ratePercent(item.ok_rate)" size="sm" />
                            <span class="ml-2">{{ formatRate(item.ok_rate) }}</span>
                        </div>
                    </td>
                </template>
                <template #rejected_rate="{ item }">
                    <td>
                        <div class="d-flex align-items-center">
                            <ChartGauge
                                :value="ratePercent(item.rejected_rate)"
                                :reverse="true"
                                size="sm"
                            />
                            <span class="ml-2">
                                {{ formatRate(item.rejected_rate) }}
                            </span>
                        </div>
                    </td>
                </template>
                <template #_actions="{ item }">
                    <td class="text-right">
                        <!--
                            `@click.native.stop` en los dos: la fila despliega
                            los motivos al clicarla, y sin esto navegar
                            desplegaria la fila a la vez.
                        -->
                        <template v-if="item.client_uuid">
                            <RouterLink
                                :to="outputLeadsRoute(item)"
                                v-c-tooltip="$t('leads')"
                                class="output-performance__action"
                                @click.native.stop
                            >
                                <CIcon
                                    :content="$options.icons.cilPeople"
                                />
                            </RouterLink>
                            <RouterLink
                                :to="outputPendingsRoute(item)"
                                v-c-tooltip="$t('pendings')"
                                class="output-performance__action"
                                @click.native.stop
                            >
                                <CIcon :content="$options.icons.cilClock" />
                            </RouterLink>
                        </template>
                    </td>
                </template>
                <template #details="{ item }">
                    <CCollapse :show="isExpanded(item)">
                        <CCardBody>
                            <p
                                v-if="!hasReasons(item)"
                                class="output-performance__empty mb-0"
                            >
                                {{ $t("no_rejection_reasons") }}
                            </p>
                            <table
                                v-else
                                class="table table-sm mb-0 output-performance__reasons"
                            >
                                <thead>
                                    <tr>
                                        <th>{{ $t("outcome") }}</th>
                                        <th>{{ $t("http_status") }}</th>
                                        <th>{{ $t("message") }}</th>
                                        <th class="text-right">
                                            {{ $t("count") }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(reason, index) in item
                                            .top_reasons"
                                        :key="index"
                                        :class="
                                            isOther(reason)
                                                ? 'output-performance__reasons--other'
                                                : ''
                                        "
                                    >
                                        <td>
                                            {{
                                                reason.outcome
                                                    ? $t(reason.outcome)
                                                    : "—"
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                reason.http_status === null ||
                                                reason.http_status === undefined
                                                    ? "—"
                                                    : reason.http_status
                                            }}
                                        </td>
                                        <td>{{ reasonMessage(reason) }}</td>
                                        <td class="text-right">
                                            {{ toCount(reason.count) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </CCardBody>
                    </CCollapse>
                </template>
            </CDataTable>
        </CCardBody>
        <CElementCover
            :opacity="0.8"
            class="bg-trasparent"
            v-if="loading"
        />
    </CCard>
</template>

<script>
/**
 * Components
 */
import ChartGauge from "£lement/ChartGauge";

/**
 * Others
 */
import { cilPeople, cilClock } from "@coreui/icons";

/**
 * Mixins
 */
import traslateMixin from "~/mixins/traslate.js";

export default {
    name: "BalancerOutputsPerformance",
    components: {
        ChartGauge,
    },
    icons: {
        cilPeople,
        cilClock,
    },
    mixins: [traslateMixin],
    props: {
        /**
         * `outputs` tal cual lo devuelve
         * `/shipment.balancing.api/client-groups/{uuid}/outputs/performance`.
         */
        outputs: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Status HTTP, mensaje o `true` si la llamada fallo. Se pinta en vez de
         * dejar el panel vacio, que no distingue "sin datos" de "no responde".
         */
        error: {
            type: [Number, String, Boolean],
            default: null,
        },
    },
    data() {
        return {
            /**
             * Salidas desplegadas, por `client_id`. Se guarda aqui en vez de en
             * los items para no mutar la prop.
             */
            expanded: {},
            // `attempts` son los envios reales y el denominador de las tasas;
            // ok/rejected/duplicate/error/capped son su desglose por outcome.
            // `duplicate` es contador propio: antes iba dentro de `rejected`,
            // que ahora es solo KO real.
            //
            // `pending` NO entra en `attempts`: son leads parados en la tabla
            // `delivery_pending` (reproceso manual), que todavia no tienen
            // resultado. Por eso no participa en el cuadre con `attempts`.
            fields: [
                { key: "client_id", label: "#" },
                { key: "client_name", label: "output" },
                // El campo es `attempts`, pero se lee "Total".
                { key: "attempts", label: "total_attempts" },
                { key: "ok", label: "ok" },
                { key: "rejected", label: "rejected" },
                { key: "duplicate", label: "duplicate" },
                { key: "error", label: "error" },
                { key: "capped", label: "capped" },
                { key: "pending", label: "pending" },
                { key: "ok_rate", label: "conversion_(%)" },
                { key: "rejected_rate", label: "rejection_(%)" },
                {
                    key: "_actions",
                    label: "",
                    sorter: false,
                    filter: false,
                    _style: "width:5%",
                    _classes: "text-right",
                },
            ],
        };
    },
    computed: {
        /**
         * Filas con los contadores y las tasas casteados a numero.
         *
         * No es cosmetico: `CDataTable` ordena con
         * `typeof value === "number" ? value : String(value).toLowerCase()`,
         * asi que con los contadores como string —que es como los sirve este
         * endpoint segun el contrato— el orden saldria alfabetico y "10" iria
         * antes de "2".
         */
        rows: function () {
            return this.outputs.map((output) => ({
                ...output,
                attempts: this.toCount(output.attempts),
                ok: this.toCount(output.ok),
                rejected: this.toCount(output.rejected),
                duplicate: this.toCount(output.duplicate),
                error: this.toCount(output.error),
                capped: this.toCount(output.capped),
                pending: this.toCount(output.pending),
                ok_rate: this.toRate(output.ok_rate),
                rejected_rate: this.toRate(output.rejected_rate),
            }));
        },
    },
    watch: {
        // Al recargar con otra ventana de fechas las salidas cambian, y dejar
        // desplegada una que ya no viene deja el estado colgado.
        outputs() {
            this.expanded = {};
        },
    },
    methods: {
        toCount(value) {
            const count = parseInt(value, 10);
            return isNaN(count) ? 0 : count;
        },
        toRate(value) {
            const rate = parseFloat(value);
            return isNaN(rate) ? null : rate;
        },
        /**
         * Las tasas de este endpoint llegan como fraccion (`0.4` = 40 %), no
         * como porcentaje. No confundir con el `formatPercent` de la pagina de
         * detalle, que recibe ya 0..100.
         */
        formatRate(value) {
            const rate = this.toRate(value);
            return rate === null ? "-" : `${(rate * 100).toFixed(1)}%`;
        },
        /**
         * `ChartGauge` espera 0..100; las tasas llegan como fraccion.
         */
        ratePercent(value) {
            const rate = parseFloat(value);
            return isNaN(rate) ? 0 : rate * 100;
        },
        /**
         * Leads y pendientes de la salida. Son rutas de la campana de entrega,
         * asi que muestran todo lo de esa salida, no solo lo que le mando este
         * balanceador: el endpoint no devuelve el uuid del miembro del grupo,
         * que es lo que acotaria por balanceador.
         */
        outputLeadsRoute(output) {
            return {
                name: "delivery_campaigns_leads",
                params: { uuid: output.client_uuid },
            };
        },
        outputPendingsRoute(output) {
            return {
                name: "delivery_pending",
                params: { uuid: output.client_uuid },
            };
        },
        /**
         * Ficha de la salida. Es una campana de entrega, asi que va a la misma
         * ruta que usan las salidas del listado de balanceadores, por uuid.
         */
        outputRoute(output) {
            return {
                name: "update_delivery_campaign",
                params: { uuid: output.client_uuid },
                query: { view_type: "edit" },
            };
        },
        hasReasons(item) {
            return Array.isArray(item.top_reasons) && item.top_reasons.length > 0;
        },
        isExpanded(item) {
            return Boolean(this.expanded[item.client_id]);
        },
        toggleReasons(item) {
            this.$set(
                this.expanded,
                item.client_id,
                !this.expanded[item.client_id]
            );
        },
        /**
         * El contrato agrupa en una sola entrada todo lo que queda fuera de los
         * `top_reasons` pedidos, con `outcome` y `http_status` nulos.
         */
        isOther(reason) {
            return (
                (reason.outcome === null || reason.outcome === undefined) &&
                (reason.http_status === null ||
                    reason.http_status === undefined)
            );
        },
        reasonMessage(reason) {
            return this.isOther(reason)
                ? this.$t("other_reasons")
                : reason.message;
        },
    },
};
</script>

<style scoped lang="scss">
.output-performance {
    &__caret {
        color: #768192;
        font-size: 0.7rem;
        margin-right: 0.25rem;

        &--empty {
            opacity: 0.35;
        }
    }

    &__action {
        margin-left: 0.5rem;
        color: #768192;

        &:hover {
            color: #117a8b;
        }
    }

    &__hint {
        margin-left: 0.2rem;
        opacity: 0.55;
        cursor: help;
    }

    &__empty {
        font-size: 0.8rem;
        color: #768192;
    }

    &__reasons {
        font-size: 0.8rem;

        &--other {
            color: #768192;
            font-style: italic;
        }
    }
}
</style>
